import {Component, CUSTOM_ELEMENTS_SCHEMA, effect, forwardRef, inject, model, signal, viewChild} from '@angular/core';
import {LoadedPolicy, loadPolicy} from '@open-policy-agent/opa-wasm';
import {MonacoEditorComponent} from '../components/monaco-editor/monaco-editor.component';
import {FormsModule} from '@angular/forms';
import {AccessControlService} from '../security/access-control.service';
import {Portfolio, PortfolioStatusEnum} from '../../api-client-kt/src';
import {LobOfficePermission} from '../security/subject';
import {Dropdown, Radio} from '@fluentui/web-components';


const accountDataJson0 = `
{
  "id": "cf0ede98-b456-4b78-baa1-0762fdd62e1f",
  "status": "Open",
  "account": "Acme Ltd",
  "aptp": 0.366,
  "calculatedPilStatus": "Improve",
  "renewalStatus": "Renewed",
  "renewalAgoraStatus": "Bound",
  "renewalAgoraSubStatus": "Bound",
  "renewalAptp": 0.1025,
  "renewalGnwp": 69750,
  "renewalGnwpEur": 69750,
  "renewalGwp": 69750,
  "renewalGwpEur": 69750,
  "renewalInceptionDate": "2026-03-31T00:00:00.000Z",
  "renewalLimit": 30000000,
  "renewalRunningDays": 365,
  "renewalShare": 0.25
}
`;

const segmentDataJson0 = `
{
  "id": "ab8e7d57-b7e7-4973-8605-265cc98ac030",
  "officeKey": "ITA",
  "lobKey": "PRP",
  "portfolioId": "1ebcf4d6-c440-403f-9b9c-758e1839c8fe",
  "status": "Open"
}
`;

const portfolio: Portfolio = {
    id: '1ebcf4d6-c440-403f-9b9c-758e1839c8fe',
    uwyear: 2026,
    description: 'portfolioDescription',
    phase: "Tracking",
    status: PortfolioStatusEnum.Active,

}

@Component({
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        MonacoEditorComponent,
        FormsModule
    ],
    template: `
        <div class="fullsize-hbox" style="height: 400px; max-width: 800px; resize: vertical; overflow: auto; padding: 0 5px 5px 0;">
            <div class="fullsize-vbox">
                <h4>Segment</h4>
                <app-monaco-editor #segmentEditor language="json" [showToolbar]="false" [initialValue]="segmentDataJson"></app-monaco-editor>
            </div>
            <div class="fullsize-vbox">
                <h4>Account</h4>
                <app-monaco-editor #accountEditor language="json" [showToolbar]="false" [initialValue]="accountDataJson"></app-monaco-editor>
            </div>
        </div>
        <div style="margin-top: 10px">
            <h4>User Roles</h4>
            <div class="fullsize-hbox" style="gap: 10px;">
                <fluent-radio-group name="role" (change)="onRoleChange($event)">
                    <fluent-field label-position="after">
                        <label slot="label" for="role_devops">DevOps</label>
                        <fluent-radio slot="input" name="role" id="role_devops" value="devops"></fluent-radio>
                    </fluent-field>

                    <fluent-field label-position="after">
                        <label slot="label" for="role_ct_techex">Central TechEx</label>
                        <fluent-radio slot="input" name="role" id="role_ct_techex" value="ct_techex"></fluent-radio>
                    </fluent-field>

                    <fluent-field label-position="after">
                        <label slot="label" for="role_local_techex">Local TechEx</label>
                        <fluent-radio slot="input" name="role" id="role_local_techex" value="local_techex"></fluent-radio>
                    </fluent-field>

                    <fluent-field label-position="after">
                        <label slot="label" for="role_local_lh">Local LoB Head</label>
                        <fluent-radio slot="input" name="role" id="role_local_lh" value="local_lh"></fluent-radio>
                    </fluent-field>

                    <fluent-field label-position="after">
                        <label slot="label" for="role_local_uw">Local Underwriter</label>
                        <fluent-radio slot="input" name="role" id="role_local_uw" value="local_uw"></fluent-radio>
                    </fluent-field>
                </fluent-radio-group>
            </div>
        </div>
        <div style="margin-top: 10px">
            <h4>Account Attribute</h4>
            <fluent-dropdown placeholder="Attribute to test" type="combobox" appearance="outline" size="small" value="Status" (change)="onAttributeChange($event)">
                <fluent-listbox popover="manual">
                    <fluent-option value="account">Account Name</fluent-option>
                    <fluent-option value="status" selected>Status</fluent-option>
                    <fluent-option value="renewalStatus" selected>Renewal Status</fluent-option>
                    <fluent-option value="renewalAptp">Renewal APTP</fluent-option>
                </fluent-listbox>
            </fluent-dropdown>
        </div>
        <div style="margin-top: 10px; align-items: center" class="form-1-column">
            <label>Edit Account</label>
            <div>{{canEditAccount() ?? '-'}}</div>
            <label>Edit Account Status</label>
            <div>{{canEditAccountStatus() ?? '-'}}</div>
            <label>Edit Account Attribute</label>
            <div>{{canEditAccountAttribute() ?? '-'}}</div>
            <label>Edit Segment</label>
            <div>{{canEditSegment() ?? '-'}}</div>
            <label>Edit Segment Status</label>
            <div>{{canEditSegmentStatus() ?? '-'}}</div>
        </div>
        <fluent-button (click)="test()">Evaluate</fluent-button>
    `,
    styles: `
        h4 {
            margin: 5px 0;
        }
    `
})
export class TestOpaWasmComponent {

    protected readonly accountDataJson = sessionStorage.getItem('test.opa.accountJson') ?? accountDataJson0;
    protected readonly segmentDataJson = sessionStorage.getItem('test.opa.segmentJson') ?? segmentDataJson0;

    protected readonly role = signal<string>('local_lh');
    protected readonly attribute = signal<string>('status');

    private readonly segmentEditor = viewChild.required<MonacoEditorComponent>('segmentEditor');
    private readonly accountEditor = viewChild.required<MonacoEditorComponent>('accountEditor');

    private policy!: LoadedPolicy;

    protected readonly canEditAccount = signal<boolean | undefined>(undefined);
    protected readonly canEditAccountStatus = signal<boolean | undefined>(undefined);
    protected readonly canEditAccountAttribute = signal<boolean | undefined>(undefined);
    protected readonly canEditSegment = signal<boolean | undefined>(undefined);
    protected readonly canEditSegmentStatus = signal<boolean | undefined>(undefined);

    private readonly accessControlSvc = new AccessControlService();

    constructor() {
        effect(() => this.test());
    }

    async ngOnInit(): Promise<void> {
        const policyRequest = await fetch('/opa/pfm_app.wasm')
        this.policy = await loadPolicy(await policyRequest.arrayBuffer());
        console.log(this.policy);
    }

    test() {
        try {
            console.log(`Evaluating permissions for ${this.role()} and ${this.attribute()}`);

            const role = this.role();
            const attr = this.attribute();
            const accountJson = this.accountEditor().getValue();
            const segmentJson = this.segmentEditor().getValue();
            if (!accountJson || !segmentJson || !role || !attr) return;

            const account = JSON.parse(accountJson);
            const segment = JSON.parse(segmentJson);

            const lobOfficePerms: LobOfficePermission[] = [{
                lobKey: segment.lobKey,
                officeKey: segment.officeKey,
                allow: true
            }];

            this.accessControlSvc.userContext.set({
                userId: 'test',
                userName: 'test',
                roles: [role],
                lobOfficePermissions: lobOfficePerms
            });

            const canEditAccount = this.accessControlSvc.canEditAccount(account, segment, portfolio);
            const canEditAccountStatus = this.accessControlSvc.canEditAccountStatus(account, segment, portfolio);
            const canEditAccountAttribute = this.accessControlSvc.canEditAccountAttribute(account, attr, segment, portfolio);
            const canEditSegment = this.accessControlSvc.canEditSegment(segment, portfolio);
            const canEditSegmentStatus = this.accessControlSvc.canEditSegmentStatus(segment, portfolio);

            this.canEditAccount.set(canEditAccount.allowed);
            this.canEditAccountStatus.set(canEditAccountStatus.allowed);
            this.canEditAccountAttribute.set(canEditAccountAttribute.allowed);
            this.canEditSegment.set(canEditSegment.allowed);
            this.canEditSegmentStatus.set(canEditSegmentStatus.allowed);

            sessionStorage.setItem('test.opa.accountJson', this.accountEditor().getValue());
            sessionStorage.setItem('test.opa.segmentJson', this.segmentEditor().getValue());
        } catch (error) {
            console.warn(error);
        }
    }

    protected onRoleChange(event: Event) {
        const role = (event.target as Radio).value;
        this.role.set(role);
    }

    protected onAttributeChange(event: Event) {
        const attr = (event.target as Dropdown).value;
        if (attr) this.attribute.set(attr);
    }
}
